// Carga de prendas por lote: el camino principal de la app.
//
//   elegís N fotos → se achican acá → suben y las clasifica la IA
//   → aparece una grilla de fichas ya llenas → corregís lo que esté mal
//   → un solo "Guardar todo"
//
// Decisiones de diseño, todas por el mismo motivo — que cargar el
// armario no dé pereza:
//
//   - La miniatura aparece apenas elegís el archivo, antes de subir.
//     Ver la foto al instante hace que la espera se sienta corta.
//   - Se procesa de a 6, no las 30 juntas: las primeras fichas aparecen
//     mientras las demás siguen viajando.
//   - La edición es en línea sobre la ficha. Veinte formularios en fila
//     no los completa nadie.
//   - Una foto que falla no tumba la tanda y se puede reintentar sola.
//   - Las fichas de confianza baja se marcan, para saber cuáles mirar
//     en vez de desconfiar de las veinte por igual.

import { useEffect, useRef, useState } from "react";
import * as api from "./api";
// Para distinguir "esta foto falló" de "no tiene sentido seguir".
import { esSesionVencida } from "./api";
import { prepararVarias } from "./imagen";
import {
  cargarOpciones,
  zonasDe,
  expandirEstaciones,
  atajoDeEstaciones,
} from "./opciones";
import Visor from "./Visor";
import { t, tCat, tPlural, useIdioma, tError } from "./i18n";

// De a 6 por request. Suficientemente chico para que las fichas
// aparezcan de a poco, suficientemente grande para no hacer 30 viajes.
const POR_VIAJE = 6;

let contador = 0;
const nuevoId = () => `ficha-${++contador}`;

/* ══════════════════════════════════════════════════════════════════
   EL BORRADOR: NO PERDER UNA TANDA POR CAMBIAR DE PESTAÑA
   ══════════════════════════════════════════════════════════════════

   `App.js` DESMONTA este componente al cambiar de pestaña y las fichas
   viven solo en `useState`. El que revisó dieciocho fichas y tocó "Mi
   armario" para chequear un color volvía a una pantalla vacía —y las
   fotos ya subidas quedaban en Storage sin ninguna fila que las
   referenciara, porque la limpieza depende de que el cliente llame a
   descartar y al desmontar no llama nadie.

   Era el hallazgo AL-8 de la auditoría y llevaba abierto desde
   entonces. La valija ya había resuelto el mismo problema, con la
   misma lección escrita en su cabecera: PERDER EL LUGAR ES LO QUE HACE
   QUE UNO NO SE MOLESTE EN CORREGIR UN DATO.

   ⚠️  NO SE GUARDA `vistaPrevia`, Y NO ES UN OLVIDO.

   Es el data-URL de la foto achicada a 1024 px: unos 200-400 KB por
   ficha. Con las 30 de una tanda son 6-12 MB y la cuota de
   localStorage es ~5 MB. Guardarla haría que el borrador FALLE al
   escribir justo en la tanda grande — la única donde perder el trabajo
   duele de verdad.

   En su lugar viaja `foto_url`, la URL firmada que ya devuelve
   `/analizar` (unos 300 caracteres), con la marca de cuándo se firmó.
   El precio, escrito: **las firmas duran una hora**. Si volvés después,
   las fichas están enteras con todas tus correcciones y la foto se
   reemplaza por un cartel. Se prefirió eso a inventar un endpoint que
   firme rutas arbitrarias, que es superficie nueva de seguridad para
   un problema de comodidad.

   Y las fotos ya no quedan huérfanas: mientras el borrador exista, la
   tanda tiene dueño. */
const CLAVE_BORRADOR = "brujel.carga.borrador";
const VERSION_BORRADOR = 1;

// La firma dura una hora; se corta antes para no mostrar una URL que
// vence mientras la mirás.
const MINUTOS_DE_FIRMA = 50;

/* Un borrador viejísimo es una tanda abandonada. Se tira, y al tirarla
 * se borran sus fotos de Storage — que es la otra mitad de AL-8. Siete
 * días es holgado: nadie deja una carga a medias dos semanas y vuelve
 * esperando encontrarla. */
const DIAS_DE_VIDA = 7;

function leerBorrador() {
  try {
    const crudo = localStorage.getItem(CLAVE_BORRADOR);
    if (!crudo) return null;
    const b = JSON.parse(crudo);
    /* Se valida la FORMA, no solo que exista. Un borrador de una
     * versión vieja de la app —o tocado a mano— rompería la pantalla al
     * arrancar, que es el peor momento posible para romperse. Misma
     * regla que en la valija. */
    if (!b || typeof b !== "object") return null;
    if (b.v !== VERSION_BORRADOR) return null;
    if (!Array.isArray(b.fichas)) return null;
    if (typeof b.guardadoEn !== "number") return null;
    const fichas = b.fichas.filter(
      (f) => f && typeof f.id === "string" && typeof f.foto_path === "string" && f.prenda
    );
    if (!fichas.length) return null;
    return { ...b, fichas };
  } catch {
    return null;
  }
}

function guardarBorrador(fichas) {
  try {
    /* Solo las que están LISTAS. Una ficha "subiendo" describe un
     * request en vuelo que no sobrevive al desmontaje, y una con error
     * no llegó a subir nada: no hay qué guardar ni qué limpiar. */
    const guardables = fichas
      .filter((f) => f.estado === "lista" && f.foto_path)
      .map((f) => ({
        id: f.id,
        foto_path: f.foto_path,
        foto_mini_path: f.foto_mini_path || null,
        foto_url: f.foto_url || null,
        firmadaEn: f.firmadaEn || null,
        /* ⚠️  LA MINIATURA SÍ ENTRA AL BORRADOR (2026-08-29), Y ESTO
         * CAMBIA UNA DECISIÓN VIEJA A PROPÓSITO.
         *
         * La cabecera de arriba explica por qué `vistaPrevia` NO se
         * guarda: 200-400 KB por ficha × 30 revientan la cuota de
         * localStorage (~5 MB). Eso sigue siendo cierto y sigue afuera.
         *
         * Pero el precio de no guardar NADA lo pagaba el celular: ahí
         * el navegador descarta la página con cambiar de app —o con
         * abrir la galería para elegir las fotos—, el componente se
         * remonta, restaura del borrador… y las fotos no están. La
         * restauración caía a la URL firmada, que vence a la hora, y el
         * usuario veía "Foto subida (no se puede mostrar hasta guardar)"
         * en cada ficha. En desktop nadie lo vio porque la pestaña
         * sobrevive; en el teléfono pasa todo el tiempo.
         *
         * La miniatura de 512px son ~30-50 KB: la tanda entera de 30 es
         * ~1,5 MB, y ya estaba calculada de antes. Si igual no entra en
         * la cuota, el catch de abajo hace lo de siempre: la carga
         * funciona, el borrador no. */
        miniPrevia: f.miniPrevia || null,
        nombreArchivo: f.nombreArchivo || null,
        confianza: f.confianza || null,
        prenda: f.prenda,
      }));
    if (!guardables.length) {
      localStorage.removeItem(CLAVE_BORRADOR);
      return;
    }
    localStorage.setItem(
      CLAVE_BORRADOR,
      JSON.stringify({ v: VERSION_BORRADOR, guardadoEn: Date.now(), fichas: guardables })
    );
  } catch {
    /* Modo privado, cuota llena, lo que sea. Guardar el borrador es una
     * comodidad; que la carga no se pueda hacer, no. */
  }
}

function olvidarBorrador() {
  try {
    localStorage.removeItem(CLAVE_BORRADOR);
  } catch {
    // Ídem.
  }
}

export default function CargaLote({ alGuardar, recienGuardadas = [] }) {
  // Re-renderiza si cambia el idioma: `tCat` se resuelve en el render.
  useIdioma();
  const [opciones, setOpciones] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [avance, setAvance] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  // Cuántas fichas se recuperaron de una tanda anterior. Solo para
  // avisar: si no se dice, el usuario cree que subió algo dos veces.
  const [restauradas, setRestauradas] = useState(0);

  const inputRef = useRef(null);
  /* Segundo input, con `capture`: en un teléfono abre la cámara directo
   * en vez del selector de archivos. En escritorio el navegador lo
   * ignora y muestra el selector: no rompe nada, solo no atajа. Es un
   * input aparte porque `capture` es un atributo del input, no una
   * opción del clic, y un solo input no puede ser las dos cosas. */
  const camaraRef = useRef(null);
  /* «Quitar todas» sin cartel de «¿estás seguro?»: se quitan y queda un
   * «Deshacer» unos segundos, igual que el borrado en Mi armario. */
  const [quitadas, setQuitadas] = useState(null);
  const relojDeshacer = useRef(null);

  useEffect(() => {
    cargarOpciones().then(setOpciones);
  }, []);

  /* Restaurar la tanda que quedó a medias. Corre UNA vez, al montar, y
   * antes de que el usuario pueda tocar nada. */
  useEffect(() => {
    const b = leerBorrador();
    if (!b) return;

    const edad = Date.now() - b.guardadoEn;

    if (edad > DIAS_DE_VIDA * 24 * 60 * 60 * 1000) {
      /* Tanda abandonada. Se tira Y se borran sus fotos: es la otra
       * mitad de AL-8 —las fotos huérfanas— y este es el único momento
       * en que alguien puede hacerlo, porque solo el cliente sabe que
       * esas rutas nunca llegaron a ser una prenda. Si falla, no se le
       * avisa a nadie: es basura nuestra. */
      for (const f of b.fichas) {
        api.descartarFoto(f.foto_path, f.foto_mini_path).catch(() => {});
      }
      olvidarBorrador();
      return;
    }

    setFichas(
      b.fichas.map((f) => ({
        ...f,
        estado: "lista",
        /* La firma vence a la hora. Si sigue viva se reusa; si no, la
         * ficha va sin foto y lo dice, en vez de dibujar un <img> roto
         * que parece un error de la app. */
        /* La miniatura guardada primero: no vence nunca y no baja nada
         * de la red. La URL firmada queda de respaldo para borradores
         * anteriores a que la miniatura entrara (no tienen miniPrevia). */
        vistaPrevia:
          f.miniPrevia ||
          (f.foto_url && f.firmadaEn && Date.now() - f.firmadaEn < MINUTOS_DE_FIRMA * 60 * 1000
            ? f.foto_url
            : null),
        /* ⚠️  POR QUÉ NO HAY FOTO, Y NO SOLO QUE NO HAY.
         *
         * Antes había UN cartel para tres situaciones distintas, y las
         * tres se leen igual desde afuera: "no aparecen las fotos". Sin
         * distinguirlas, la única forma de avanzar es que yo adivine —
         * y ya adiviné mal una vez con esto.
         *
         *   borradorViejo → el borrador se escribió antes de que la
         *                   miniatura entrara (deploy del 2026-08-29).
         *                   Se arregla solo en la tanda siguiente.
         *   firmaVencida  → borrador nuevo pero de hace más de una hora
         *                   y sin miniatura: no debería pasar.
         *   (nada)        → hay imagen; si igual no se ve, el <img>
         *                   falló y lo dice el onError. */
        sinFoto: f.miniPrevia
          ? null
          : f.foto_url
            ? "firmaVencida"
            : "borradorViejo",
      }))
    );
    setRestauradas(b.fichas.length);
  }, []);

  /* Escribir el borrador en UN solo lugar.
   *
   * Podría escribirse en `sacar`, en `guardarTodo`, en cada `onCambiar`
   * y en el cierre de cada tanda. Serían cinco lugares y el bug sería
   * olvidarse uno — que es exactamente cómo se rompió el botón de
   * fichada dos veces el mismo día. Acá el borrador es una función del
   * estado, no una consecuencia de cada acción. */
  useEffect(() => {
    guardarBorrador(fichas);
  }, [fichas]);

  /* ---------------- elegir y procesar ---------------- */

  async function recibirArchivos(lista) {
    const archivos = Array.from(lista || []);
    if (!archivos.length) return;

    setError("");
    setProcesando(true);

    try {
      const tope = opciones?.maxPorTanda || 30;
      const disponibles = tope - fichas.length;

      if (disponibles <= 0) {
        setError(t("carga.yaTienes", "Ya tienes {n} fichas. Guarda o saca algunas antes de sumar más.", { n: fichas.length }));
        return;
      }

      const aProcesar = archivos.slice(0, disponibles);
      if (archivos.length > disponibles) {
        setError(
          t("carga.seTomanPrimeras", "Se toman las primeras {n}: el máximo por tanda es {max}.", { n: disponibles, max: tope })
        );
      }

      // 1. Achicar en el navegador
      setAvance({ etapa: "achicando", hechas: 0, total: aProcesar.length });
      const { listas, fallidas } = await prepararVarias(aProcesar, (hechas, total) =>
        setAvance({ etapa: "achicando", hechas, total })
      );

      if (fallidas.length) {
        setError(fallidas.map((f) => f.error).join(" · "));
      }
      if (!listas.length) return;

      // 2. Fichas provisorias: la foto ya se ve, todavía sin datos
      const provisorias = listas.map((f) => ({
        id: nuevoId(),
        estado: "subiendo",
        vistaPrevia: f.vistaPrevia,
        /* La miniatura de 512px (~30-50 KB), que ya está calculada
         * porque viaja al servidor. Se guarda en la ficha para el
         * BORRADOR: es la que sobrevive al descarte de la página. Ver
         * el comentario en guardarBorrador. */
        miniPrevia: f.mini ? `data:image/jpeg;base64,${f.mini.base64}` : null,
        nombreArchivo: f.nombreArchivo,
      }));
      setFichas((antes) => [...antes, ...provisorias]);

      // 3. Subir y clasificar, de a tandas chicas
      let hechas = 0;
      for (let i = 0; i < listas.length; i += POR_VIAJE) {
        const trozo = listas.slice(i, i + POR_VIAJE);
        const idsDelTrozo = provisorias.slice(i, i + POR_VIAJE).map((p) => p.id);

        setAvance({ etapa: "analizando", hechas, total: listas.length });

        try {
          const { fichas: recibidas } = await api.analizarFotos(
            // `mini` viaja con la foto: subir las dos en el mismo viaje
            // evita una segunda vuelta y garantiza que si la grande se
            // guardó, la chica también se intentó.
            trozo.map((f) => ({
              base64: f.base64,
              contentType: f.contentType,
              mini: f.mini || null,
            }))
          );

          setFichas((antes) =>
            antes.map((ficha) => {
              const pos = idsDelTrozo.indexOf(ficha.id);
              if (pos === -1) return ficha;

              const r = recibidas[pos];
              if (!r?.ok) {
                /* ⚠️  `tError` Y NO `r.error` (auditoría 2026-09-15, §3).
                 * El servidor manda `{ codigo, error, datos }`; `error`
                 * es el respaldo en inglés y `codigo` es lo que esta
                 * pantalla traduce. Antes se dibujaba el respaldo tal
                 * cual —y durante un tiempo fue texto en español con el
                 * mensaje crudo de Storage adentro. */
                return {
                  ...ficha,
                  estado: "error",
                  error: r?.codigo || r?.error
                    ? tError(r)
                    : t("carga.noSePudoProcesar", "no se pudo procesar"),
                };
              }

              return {
                ...ficha,
                estado: "lista",
                foto_path: r.foto_path,
                // Puede venir null y está bien: la prenda se guarda sin
                // miniatura y la grilla cae a la grande.
                foto_mini_path: r.foto_mini_path || null,
                /* La URL firmada NO se usa ahora —la vista previa local
                 * es la misma imagen, ya en memoria— pero se guarda
                 * para el borrador: es la única forma de volver a ver
                 * la foto después de cambiar de pestaña, porque el
                 * data-URL no entra en localStorage. Ver el bloque del
                 * borrador arriba. */
                foto_url: r.foto_url || null,
                firmadaEn: Date.now(),
                /* ⚠️  ACÁ SE VOLVÍA A BAJAR CADA FOTO RECIÉN SUBIDA.
                 *
                 * Decía `vistaPrevia: r.foto_url || ficha.vistaPrevia`:
                 * reemplazaba la vista previa LOCAL —que es la misma
                 * imagen, ya en memoria— por la URL firmada del
                 * servidor. O sea que el navegador descargaba de vuelta
                 * los 124 KB de cada foto que acababa de subir. Una
                 * tanda de 6 eran 750 KB de egreso por nada.
                 *
                 * La local además es MEJOR para este uso: no vence. La
                 * firmada dura una hora, y una ficha puede quedar
                 * abierta más que eso mientras se corrigen 30 prendas.
                 *
                 * Se conserva tal cual vino de `prepararFoto`. */
                vistaPrevia: ficha.vistaPrevia,
                confianza: r.confianza,
                /* El aviso de "esta ya la tenés". Viaja al lado de la
                 * ficha y NO adentro de `prenda`: lo que está en
                 * `prenda` se reenvía tal cual al guardar. */
                yaEnArmario: r.yaEnArmario || null,
                avisos: r.avisos || [],
                camposDudosos: r.camposDudosos || [],
                errorIA: r.errorIA || null,
                prenda: r.prenda,
              };
            })
          );
        } catch (e) {
          setFichas((antes) =>
            antes.map((ficha) =>
              idsDelTrozo.includes(ficha.id)
                ? { ...ficha, estado: "error", error: tError(e) }
                : ficha
            )
          );

          /* ⚠️  SE CORTA EL BUCLE. NO SE SIGUE (2026-08-24, CR-8).
           *
           * El `catch` marcaba la tanda como fallida y SEGUÍA con las
           * siguientes. Para un error puntual —una foto que la IA no
           * pudo leer— está perfecto: las demás tienen que procesarse.
           *
           * Pero hay dos errores donde seguir es tirar plata y datos:
           *
           *   SESIÓN VENCIDA: el token ya no sirve y no se puede
           *   renovar. Las tandas que quedan van a fallar TODAS, y
           *   mientras tanto se suben megabytes por datos móviles para
           *   recibir el mismo 401 una y otra vez.
           *
           *   FRENO DE FRECUENCIA (429): el servidor dijo "esperá". Es
           *   literalmente una instrucción, y seguir mandando la ignora.
           *
           *   EL PLAN NO DEJA (403, agregado el 2026-09-06 por la
           *   auditoría del 5/9): el plan venció o se llegó al tope de
           *   prendas. Es la misma clase que las otras dos y faltaba.
           *   Alguien vencido eligiendo 30 fotos subía SEIS tandas de
           *   base64 por datos móviles, todas rechazadas por la misma
           *   razón, para terminar mirando 30 fichas rojas. El
           *   servidor ya había contestado en la primera; las otras
           *   cinco fueron megabytes de la persona gastados para que
           *   le repitieran lo mismo.
           *
           * En los tres casos las fichas que faltan quedan en su estado
           * provisorio, que es honesto: no se intentaron.
           *
           * ⚠️  403 y no "cualquier error del plan": el código no se
           *     mira acá. Un 403 es siempre "el servidor no te deja", y
           *     reintentar con las mismas credenciales y el mismo plan
           *     no va a cambiar nada, sea PLAN_EXPIRED, sea
           *     GARMENT_LIMIT_REACHED, sea uno que todavía no existe.
           */
          if (esSesionVencida(e) || e?.status === 429 || e?.status === 403) {
            setFichas((antes) =>
              antes.map((ficha) =>
                ficha.estado === "subiendo" && !idsDelTrozo.includes(ficha.id)
                  ? { ...ficha, estado: "error", error: t("carga.noSeIntento", "No se intentó: {motivo}", { motivo: tError(e) }) }
                  : ficha
              )
            );
            break;
          }
        }

        hechas += trozo.length;
      }
    } finally {
      setProcesando(false);
      setAvance(null);
      if (inputRef.current) inputRef.current.value = "";
      if (camaraRef.current) camaraRef.current.value = "";
    }
  }

  /* ---------------- editar y sacar fichas ---------------- */

  function cambiar(id, campo, valor) {
    setFichas((antes) =>
      antes.map((f) =>
        f.id === id ? { ...f, prenda: { ...f.prenda, [campo]: valor } } : f
      )
    );
  }

  function alternarLista(id, campo, valor) {
    setFichas((antes) =>
      antes.map((f) => {
        if (f.id !== id) return f;
        const actual = f.prenda[campo] || [];
        const nueva = actual.includes(valor)
          ? actual.filter((v) => v !== valor)
          : [...actual, valor];
        // Nunca dejar la lista vacía: la base exige al menos un valor.
        return nueva.length
          ? { ...f, prenda: { ...f.prenda, [campo]: nueva } }
          : f;
      })
    );
  }

  async function sacar(id) {
    const ficha = fichas.find((f) => f.id === id);
    setFichas((antes) => antes.filter((f) => f.id !== id));

    // La foto ya está en Storage: si no se borra, queda ocupando lugar
    // sin que ninguna prenda la use. Si falla, no se le avisa al
    // usuario — es basura nuestra, no un problema suyo.
    if (ficha?.foto_path) {
      api.descartarFoto(ficha.foto_path, ficha.foto_mini_path).catch(() => {});
    }
  }

  /* «Quitar todas»: la tanda entera, de una. Las fotos de Storage NO se
   * borran hasta que venza el «Deshacer»: si se borraran al instante,
   * deshacer devolvería fichas con la imagen rota. */
  function quitarTodas() {
    if (!fichas.length || procesando || guardando) return;
    const copia = fichas;
    setFichas([]);
    setQuitadas(copia);
    clearTimeout(relojDeshacer.current);
    relojDeshacer.current = setTimeout(() => {
      setQuitadas((q) => {
        if (q === copia) {
          for (const f of copia) {
            if (f.foto_path) api.descartarFoto(f.foto_path, f.foto_mini_path).catch(() => {});
          }
          return null;
        }
        return q;
      });
    }, 8000);
  }

  function deshacerQuitar() {
    if (!quitadas) return;
    clearTimeout(relojDeshacer.current);
    setFichas((antes) => [...quitadas, ...antes]);
    setQuitadas(null);
  }

  /* ---------------- guardar ---------------- */

  async function guardarTodo() {
    const listas = fichas.filter((f) => f.estado === "lista");
    if (!listas.length) return;

    setGuardando(true);
    setError("");

    try {
      await api.guardarPrendas(
        /* ⚠️  ACÁ SE ARMA LO QUE VIAJA AL SERVIDOR, Y ES LISTA BLANCA
         *     DE LOS DOS LADOS. `foto_mini_path` tiene que estar en esta
         *     línea Y en `normalizarPrenda` (lib/rutas_prendas.js). Si
         *     falta en cualquiera de las dos, la columna queda siempre
         *     en null y la grilla sigue bajando la foto grande sin que
         *     nada avise: no da error, da vacío. */
        listas.map((f) => ({
          ...f.prenda,
          foto_path: f.foto_path,
          foto_mini_path: f.foto_mini_path || null,
        }))
      );

      // Solo se sacan las que se guardaron: las que fallaron siguen ahí
      // para reintentar, sin perder lo que el usuario ya corrigió.
      const guardadas = new Set(listas.map((f) => f.id));
      setFichas((antes) => antes.filter((f) => !guardadas.has(f.id)));
      /* Lo que se guardó ya no es borrador. El `useEffect` de arriba lo
       * reescribe con lo que quede —y borra la clave si no queda nada—
       * pero se limpia también el contador del aviso: si no, "se
       * recuperaron 18 fichas" seguiría colgado después de guardarlas. */
      setRestauradas(0);

      /* La tira de "recién guardadas" se arma con `listas`, que se
       * capturó ANTES del filtrado de arriba. Leerla de `fichas`
       * después daría vacío: `setFichas` es asincrónico y para cuando
       * este renglón corre, las guardadas ya no están.
       *
       * Viaja `vistaPrevia` —la miniatura en memoria— y no `foto_url`:
       * la URL firmada vence en una hora y esta lista muere cuando se
       * cierra la app, así que la firma no aporta y el data-URL ya está
       * en memoria sin costo nuevo. */
      alGuardar?.(
        listas.length,
        listas.map((f) => ({
          id: f.id,
          nombre: f.prenda?.nombre || "",
          vistaPrevia: f.vistaPrevia || null,
        }))
      );
    } catch (e) {
      setError(tError(e));
    } finally {
      setGuardando(false);
    }
  }

  /* ---------------- render ---------------- */

  const listas = fichas.filter((f) => f.estado === "lista");
  const conErrores = fichas.filter((f) => f.estado === "error");
  const dudosas = listas.filter((f) => f.confianza === "baja");

  if (!opciones) return <p className="tenue">{t("comun.cargando", "Cargando...")}</p>;

  return (
    <section>
      {/* ⚠️  ARRIBA DE LA ZONA DE SOLTAR, Y ESO ES LA MITAD DEL ARREGLO.
        *
        * Antes, guardar te mandaba a Mi armario. Ahora te quedás acá y
        * lo primero que ves es lo que acabás de guardar: el acuse de
        * recibo y, justo abajo, la zona lista para la tanda siguiente.
        *
        * `aria-live="polite"` porque el cambio ocurre sin que se navegue
        * a ningún lado: un lector de pantalla no tendría de qué
        * enterarse. */}
      {recienGuardadas.length > 0 && (
        <div className="recien-guardadas" aria-live="polite">
          <p className="tenue texto-mini">
            {t("carga.recienGuardadas", "Recién guardadas: {prendas}", {
              prendas: tPlural("plural.prendas", recienGuardadas.length),
            })}
          </p>
          <ul className="recien-guardadas-tira">
            {recienGuardadas.map((p) => (
              <li key={p.id} title={p.nombre}>
                {p.vistaPrevia ? (
                  <img loading="lazy" decoding="async" src={p.vistaPrevia} alt={p.nombre} />
                ) : (
                  <span className="prenda-sin-foto tenue texto-mini">{p.nombre}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className={`zona-carga ${arrastrando ? "arrastrando" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          /* ⚠️  El drop tenía que frenarse igual que el botón
           * (2026-08-24, ME-12). El input y el botón se deshabilitan
           * mientras se procesa una tanda; la zona de arrastre no.
           *
           * Dos corridas concurrentes leen el mismo `fichas.length` para
           * calcular cuántas entran, se pisan el `avance` entre sí, y
           * pueden superar el tope por tanda. */
          if (procesando) return;
          setArrastrando(false);
          recibirArchivos(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => recibirArchivos(e.target.files)}
          disabled={procesando}
          style={{ display: "none" }}
        />
        <input
          ref={camaraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => recibirArchivos(e.target.files)}
          disabled={procesando}
          style={{ display: "none" }}
        />

        {/* Se avisa que la tanda se recuperó. Sin esto, veinte fichas
            que uno no acaba de subir se leen como "se subieron dos
            veces" — y el reflejo es borrarlas. */}
        {restauradas > 0 && (
          <p className="texto-mini tenue">
            {t("carga.recuperadas", "Se recuperaron {n} fichas de tu carga anterior.", {
              n: restauradas,
            })}
          </p>
        )}
        <p className="zona-titulo">{t("carga.arrastra", "Arrastra tus fotos acá")}</p>
        <p className="tenue texto-chico">
          {t("carga.hastaPorTanda", "Varias de una. Hasta {max} por tanda.", { max: opciones.maxPorTanda })}
        </p>
        <div className="opciones-chips" style={{ justifyContent: "center" }}>
          <button
            className="boton-primario boton-angosto"
            onClick={() => inputRef.current?.click()}
            disabled={procesando}
          >
            {procesando ? t("carga.procesando", "Procesando...") : t("carga.elegirFotos", "Elegir fotos")}
          </button>
          <button
            type="button"
            className="boton-secundario boton-angosto"
            onClick={() => camaraRef.current?.click()}
            disabled={procesando}
          >
            {t("carga.tomarFoto", "Tomar una foto")}
          </button>
        </div>
      </div>

      {quitadas && (
        <div className="barra-deshacer">
          {t("carga.quitadas", "Se quitaron {n} fichas.", { n: quitadas.length })}{" "}
          <button className="boton-texto" onClick={deshacerQuitar}>
            {t("carga.deshacer", "Deshacer")}
          </button>
        </div>
      )}

      {/* `aria-live="polite"` y no `alert`: el avance es información, no
          un problema. Con `polite` el lector de pantalla lo dice cuando
          termina lo que está diciendo, en vez de interrumpir. Sin esto,
          quien no ve la pantalla no se entera de que la tanda avanza
          (auditoría 2026-09-15, §3). */}
      {avance && (
        <div className="avance" aria-live="polite">
          {avance.etapa === "achicando"
            ? t("carga.achicando", "Achicando fotos {hechas}/{total}...", { hechas: avance.hechas, total: avance.total })
            : t("carga.iaMirando", "La IA está mirando {hechas}/{total}...", { hechas: avance.hechas, total: avance.total })}
        </div>
      )}

      {error && <div className="mensaje-error" role="alert">{error}</div>}

      {fichas.length > 0 && (
        <>
          <div className="barra-lote">
            <div>
              <strong>{tPlural("plural.listas", listas.length)}</strong>
              {conErrores.length > 0 && (
                <span className="tenue"> · {t("carga.conError", "{n} con error", { n: conErrores.length })}</span>
              )}
              {dudosas.length > 0 && (
                <span className="aviso-dudosa">
                  {" "}· {t("carga.paraRevisar", "{n} para revisar", { n: dudosas.length })}
                </span>
              )}
            </div>
            <button
              type="button"
              className="boton-texto"
              onClick={quitarTodas}
              disabled={procesando || guardando}
            >
              {t("carga.quitarTodas", "Quitar todas")}
            </button>
            <button
              className="boton-primario boton-angosto"
              onClick={guardarTodo}
              disabled={guardando || !listas.length}
            >
              {guardando
                ? t("carga.guardando", "Guardando...")
                : t("carga.guardarPrendas", "Guardar {prendas}", { prendas: tPlural("plural.prendas", listas.length) })}
            </button>
          </div>

          <div className="grilla-fichas">
            {fichas.map((f) => (
              <Ficha
                key={f.id}
                ficha={f}
                opciones={opciones}
                onCambiar={cambiar}
                onAlternar={alternarLista}
                onSacar={sacar}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ==================================================================
   Una ficha de la grilla
================================================================== */

function Ficha({ ficha, opciones, onCambiar, onAlternar, onSacar }) {
  const [abierta, setAbierta] = useState(false);
  const [ampliada, setAmpliada] = useState(false);
  // El <img> no pudo dibujar la fuente que se le dio. Ver el onError.
  const [falloLaImagen, setFalloLaImagen] = useState(false);

  if (ficha.estado === "subiendo") {
    return (
      <article className="ficha ficha-esperando">
        <img decoding="async" src={ficha.vistaPrevia} alt="" className="ficha-foto" />
        <div className="ficha-cuerpo">
          <p className="tenue texto-chico">{t("carga.subiendo", "Subiendo...")}</p>
        </div>
      </article>
    );
  }

  if (ficha.estado === "error") {
    return (
      <article className="ficha ficha-error">
        <img decoding="async" src={ficha.vistaPrevia} alt="" className="ficha-foto" />
        <div className="ficha-cuerpo">
          <p className="texto-chico">{ficha.nombreArchivo}</p>
          <p className="mensaje-error texto-chico" role="alert">{ficha.error}</p>
          <button className="boton-texto" onClick={() => onSacar(ficha.id)}>
            {t("carga.sacar", "Sacar")}
          </button>
        </div>
      </article>
    );
  }

  const p = ficha.prenda;
  const zonas = zonasDe(p.tipo);

  /* ⚠️  LOS CAMPOS QUE LA APP RELLENÓ SOLA SE MARCAN (auditoría
   * 2026-09-15, §3).
   *
   * Cuando el modelo no puede leer la foto —oscura, borrosa, contra un
   * fondo del mismo color— `validarSalida` pone los defaults:
   * «Superior · Negro · Casual». Eso se guardaba CON CARA DE DATO
   * LEÍDO. El servidor ya anotaba cuáles eran (`avisos`), pero esas
   * cadenas están en español, con nombres internos, y ningún JSX las
   * dibujaba: existían solo para quien mirara los logs.
   *
   * Acá no se enumeran los campos con palabras —serían seis nombres
   * más en doce idiomas— sino que se marca CADA CONTROL afectado y se
   * dice una sola frase. Señalar es más claro que listar, y el usuario
   * corrige donde está mirando. */
  const dudosos = new Set(ficha.camposDudosos || []);
  const marca = (campo) => (dudosos.has(campo) ? "campo-adivinado" : "");

  return (
    <article className={`ficha ${ficha.confianza === "baja" ? "ficha-dudosa" : ""}`}>
      {/* Acá el visor importa más que en el armario: es el momento en
          que decidís si la IA acertó, y para eso hay que ver la prenda. */}
      {/* Sin vista previa: la ficha viene de un borrador cuya URL
          firmada ya venció (duran una hora). La prenda está entera y se
          guarda igual — lo único que falta es poder MIRARLA, y se dice.
          Un <img> con src vacío dibuja el ícono de imagen rota, que se
          lee como "la app falló". */}
      {/* ⚠️  SIN `loading="lazy"`, Y ES A PROPÓSITO (2026-08-29).
          La vista previa es un data-URL: ya está en memoria, no hay
          request que diferir. `lazy` no ahorra NADA acá y le da al
          navegador permiso para postergar el dibujo — que es
          exactamente el síntoma que se está persiguiendo en el
          celular. En el armario sí corresponde: ahí las fotos vienen
          de la red. */}
      {!ficha.vistaPrevia || falloLaImagen ? (
        <div className="ficha-foto prenda-sin-foto tenue texto-mini">
          {falloLaImagen
            ? t("carga.fotoNoCargo", "La foto no se pudo mostrar acá. La prenda se guarda igual.")
            : ficha.sinFoto === "borradorViejo"
              ? t("carga.fotoBorradorViejo", "Esta ficha viene de una carga anterior y no guardó la miniatura. Se arregla en la próxima tanda.")
              : t("carga.fotoNoVisible", "Foto subida (no se puede mostrar hasta guardar)")}
        </div>
      ) : (
      <img decoding="async"
        src={ficha.vistaPrevia}
        alt={p.nombre}
        /* Sin esto, un data-URL que el navegador no puede decodificar
         * dibuja el ícono de imagen rota y NADIE se entera de por qué.
         * Con esto, la ficha lo dice. */
        onError={() => setFalloLaImagen(true)}
        className="ficha-foto prenda-foto"
        onClick={() => setAmpliada(true)}
        title={t("carga.ampliar", "Ampliar")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          /* Enter y espacio hacen lo mismo que el click, sea lo que
             sea. No se duplica la expresión del onClick: se dispara
             el click de verdad. Copiar el handler exigía parsear JSX
             con llaves anidadas, que es justamente lo que este
             proyecto ya aprendió a no hacer. */
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
      />
      )}

      <Visor
        url={ampliada ? ficha.vistaPrevia : null}
        alt={p.nombre}
        onCerrar={() => setAmpliada(false)}
      />

      <div className="ficha-cuerpo">
        {ficha.confianza === "baja" && (
          <p className="aviso-dudosa texto-chico">
            {t("carga.iaDudosa", "La IA no estaba segura con esta. Mírala bien.")}
          </p>
        )}
        {/* Va DESPUÉS de "no estaba segura" y no en su lugar: son dos
            cosas distintas. Aquella dice que el modelo dudó; esta, que
            hubo campos que directamente no pudo leer y los puso la app.
            Una foto puede tener lo segundo sin lo primero — es
            justamente el caso que no se veía. */}
        {dudosos.size > 0 && (
          <p className="aviso-dudosa texto-chico">
            {t(
              "carga.iaRelleno",
              "Lo marcado lo puse yo por defecto: la IA no lo pudo leer en la foto. Revísalo."
            )}
          </p>
        )}
        {ficha.errorIA && (
          <p className="aviso-dudosa texto-chico">
            {t("carga.iaNoPudo", "La IA no pudo verla. Completa a mano.")}
          </p>
        )}
        {/* ⚠️  AVISA, NO BLOQUEA. Dos remeras blancas iguales SON dos
          * prendas: pasa con las básicas, y decidir por el usuario le
          * impediría cargar su armario de verdad. Se dice cuál es la
          * que ya está, para que pueda mirarla antes de sacar la ficha
          * — un aviso que no deja comprobarlo obliga a adivinar. */}
        {ficha.yaEnArmario && (
          <p className="aviso-dudosa texto-chico">
            {t("carga.yaEnArmario", "Parece que ya tienes esta prenda: “{nombre}”. Míralo antes de guardar.", {
              nombre: ficha.yaEnArmario.nombre,
            })}
          </p>
        )}

        <input
          className="ficha-nombre"
          value={p.nombre}
          onChange={(e) => onCambiar(ficha.id, "nombre", e.target.value)}
          placeholder={t("carga.nombrePrenda", "Nombre de la prenda")}
        />

        {/* La marca, editable y opcional. La IA la deja vacía cuando no
            la lee con claridad —el prompt le pide callarse antes que
            inventar— así que el campo en blanco es una respuesta
            correcta y no un hueco que haya que llenar. */}
        <input
          className="ficha-marca"
          value={p.marca || ""}
          onChange={(e) => onCambiar(ficha.id, "marca", e.target.value)}
          placeholder={t("carga.marcaPrenda", "Marca (opcional)")}
          maxLength={60}
        />

        <div className="ficha-fila">
          <select
            className={marca("tipo")}
            aria-label={t("carga.tipoPrenda", "Tipo de prenda")} value={p.tipo}
            onChange={(e) => {
              const nuevoTipo = e.target.value;
              onCambiar(ficha.id, "tipo", nuevoTipo);
              // Cambiar el tipo invalida la prenda concreta: "Camisa de
              // vestir" con tipo Calzado no existe. Se limpia acá y el
              // servidor lo vuelve a hacer por su cuenta, porque la
              // coherencia no puede depender del cliente.
              const sigue = Object.values(
                opciones?.taxonomia?.[nuevoTipo] || {}
              ).some((lista) => lista.includes(p.prenda));
              if (!sigue) onCambiar(ficha.id, "prenda", "");
            }}
          >
            {opciones.tipos.map((t) => (
              <option key={t} value={t}>{tCat("tipo", t)}</option>
            ))}
          </select>

          {/* Qué es exactamente. En la revisión de una tanda esto es lo
              que la IA suele acertar y conviene poder corregir de un
              toque, así que va al lado del tipo y no escondido. */}
          <select
            className={`select-ancho ${marca("prenda")}`}
            aria-label={t("carga.queEs", "Qué es exactamente")} value={p.prenda || ""}
            onChange={(e) => onCambiar(ficha.id, "prenda", e.target.value)}
          >
            <option value="">{t("carga.sinEspecificar", "Sin especificar")}</option>
            {Object.entries(opciones?.taxonomia?.[p.tipo] || {}).map(
              ([sub, lista]) => (
                <optgroup key={sub} label={sub}>
                  {lista.map((n) => (
                    <option key={n} value={n}>{tCat("prenda", n)}</option>
                  ))}
                </optgroup>
              )
            )}
          </select>

          {/* Las estaciones son un conjunto, y acá se muestran como
              ATAJOS y no como cuatro casilleros: en la revisión de una
              tanda de 20 fotos hay que poder corregir de un toque.
              El caso raro se afina después, en la ficha de la prenda. */}
          <select
            className={marca("estaciones")}
            aria-label={t("carga.estacionesLabel", "Estaciones")}
            value={atajoDeEstaciones(p.estaciones ?? p.estacion) || "otras"}
            onChange={(e) =>
              onCambiar(
                ficha.id,
                "estaciones",
                expandirEstaciones(e.target.value)
              )
            }
          >
            {Object.keys(opciones.atajosEstacion || {}).map((nombre) => (
              <option key={nombre} value={nombre}>{tCat("preset", nombre)}</option>
            ))}
            {/* Cuando la IA devuelve una combinación que no coincide con
                ningún atajo (por ejemplo primavera+verano+otoño), el
                select tiene que poder mostrarla en vez de mentir con la
                primera opción. */}
            {!atajoDeEstaciones(p.estaciones ?? p.estacion) && (
              <option value="otras" disabled>
                {expandirEstaciones(p.estaciones ?? p.estacion)
                  .map((e2) => tCat("estacion", e2))
                  .join(", ")}
              </option>
            )}
          </select>
        </div>

        <p className="tenue texto-mini">
          {zonas.length ? t("carga.cubre", "Cubre {zonas}", { zonas: zonas.join(" + ") }) : t("carga.noOcupaZona", "No ocupa zona: es complemento")}
        </p>

        <div className="ficha-fila">
          <select
            className={marca("material")}
            aria-label={t("carga.materialLabel", "Material")} value={p.material || ""}
            onChange={(e) => onCambiar(ficha.id, "material", e.target.value || null)}
          >
            <option value="">{t("carga.material", "Material?")}</option>
            {opciones.materiales.map((m) => (
              <option key={m} value={m}>{tCat("material", m)}</option>
            ))}
          </select>

          <select
            aria-label={t("carga.estadoLabel", "Estado")}
            value={p.estado}
            onChange={(e) => onCambiar(ficha.id, "estado", e.target.value)}
          >
            {opciones.estados.map((e) => (
              <option key={e} value={e}>{tCat("estado", e)}</option>
            ))}
          </select>
        </div>

        {/* Colores y ocasiones no son controles: son etiquetas, y se
            editan abajo con «Cambiar colores y ocasiones». Se marca el
            GRUPO entero, que es lo que hay que revisar. */}
        <div className="etiquetas">
          {p.colores.map((c) => (
            <span key={c} className={`etiqueta etiqueta-color ${marca("colores")}`}>
              {tCat("color", c)}
            </span>
          ))}
          {p.categorias.map((c) => (
            <span key={c} className={`etiqueta ${marca("categorias")}`}>
              {tCat("ocasion", c)}
            </span>
          ))}
        </div>

        <button className="boton-texto texto-mini" onClick={() => setAbierta(!abierta)}>
          {abierta ? t("carga.listo", "Listo") : t("carga.cambiarColores", "Cambiar colores y ocasiones")}
        </button>

        {abierta && (
          <div className="ficha-detalle">
            <p className="texto-mini tenue">{t("carga.colores", "Colores")}</p>
            <div className="opciones-chips">
              {opciones.colores.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip ${p.colores.includes(c) ? "chip-activo" : ""}`}
                  onClick={() => onAlternar(ficha.id, "colores", c)}
                >
                  {tCat("color", c)}
                </button>
              ))}
            </div>

            <p className="texto-mini tenue">{t("carga.ocasiones", "Ocasiones")}</p>
            <div className="opciones-chips">
              {opciones.categorias.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip ${p.categorias.includes(c) ? "chip-activo" : ""}`}
                  onClick={() => onAlternar(ficha.id, "categorias", c)}
                >
                  {tCat("ocasion", c)}
                </button>
              ))}
            </div>
          </div>
        )}

        <button className="boton-texto texto-mini" onClick={() => onSacar(ficha.id)}>
          {t("carga.sacarEsta", "Sacar esta")}
        </button>
      </div>
    </article>
  );
}
